import { NoSayListResponse, tenantApi } from "../lib/tenant-api";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Edit2, ListFilter, Plus, Trash2 } from "lucide-react"
import { useState } from "react";
import { toast } from "react-toastify";
import AddNoSayModal from "./AddNoSayModal";


const NoSayList = () => {

    const queryClient = useQueryClient();
    const [selectedNoSay, setSelectedNoSay] = useState<NoSayListResponse | null>(null);
    const [createModalOpen, setCreateModalOpen] = useState(false);

    const { data: noSayList = [], error: _ } = useQuery<NoSayListResponse[], Error>({
        queryKey: ['noSayList'],
        queryFn: tenantApi.getNoSayList,
        retry: 3,
        retryDelay: 1000,
    });

    const deleteNoSayMutation = useMutation({
        mutationFn: (noSayId: string) => tenantApi.deleteNoSay(noSayId),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['noSayList'] });
        },
        onError: (err: unknown) => {
            toast.error(String((err as Error).message || err) || 'Failed to delete No-Say phrase.');
        },
    });

    const handleDeleteNoSay = (noSay: NoSayListResponse) => {
        deleteNoSayMutation.mutate(noSay.id);
    };

    const handleEditNoSay = (noSay: NoSayListResponse) => {
        setSelectedNoSay(noSay);
        setCreateModalOpen(true);
    };


    const handleCreateSuccess = () => {
        queryClient.invalidateQueries({ queryKey: ['noSayList'] });
        setSelectedNoSay(null);
    };

    return (
        <div>
            <div className="sm:flex sm:items-center">
                <div className="sm:flex-auto">
                    <h1 className="text-lg font-bold leading-7 text-gray-900 sm:truncate  sm:tracking-tight">
                        Chat Moderation
                    </h1>
                    <p className="text-sm text-gray-700">
                        Add and manage your chat moderation No-Say list. These are words or phrases that you want to filter out of Lawma's response to customer's prompts.
                    </p>
                </div>
                <div className="mt-4 sm:ml-16 sm:mt-0 sm:flex-none">
                    <button
                        type="button"
                        onClick={() => {
                            setCreateModalOpen(true)
                        }}
                        className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent  shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                    >
                        <Plus className="h-4 w-4 mr-2" />
                        Add
                    </button>
                </div>
            </div>
            <div className="mt-8 flex flex-col">
                <div className="-my-2 -mx-4 overflow-x-auto sm:-mx-6 lg:-mx-8">
                    <div className="inline-block min-w-full py-2 align-middle md:px-6 lg:px-8">
                        <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:">
                            <table className="min-w-full divide-y divide-gray-300">

                                <tbody className="bg-white divide-y divide-gray-200">
                                    {noSayList.length === 0 ? (
                                        <tr>
                                            <td colSpan={5} className="px-6 py-12 text-center">
                                                <div className="flex flex-col items-center">
                                                    <ListFilter className="h-12 w-12 text-gray-400 mb-4" />
                                                    <h3 className="text-sm font-medium text-gray-900 mb-2">No Chat Moderation Found</h3>
                                                    <p className="text-sm text-gray-500 mb-4">
                                                        Get started by adding your first No-Say phrase.
                                                    </p>
                                                    <button
                                                        type="button"
                                                        onClick={() => setCreateModalOpen(true)}
                                                        className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent  shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                                                    >
                                                        <Plus className="h-4 w-4 mr-2" />
                                                        Add a No-Say Phrase
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ) : (
                                        noSayList.map((list) => (
                                            <tr key={list.id} className="hover:bg-gray-50">

                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 font-mono">
                                                    {list.no_say}
                                                </td>


                                                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                                    <div className="flex items-center justify-end space-x-2">
                                                        <button
                                                            onClick={() => handleEditNoSay(list)}
                                                            className="text-indigo-600 hover:text-indigo-900"
                                                            title="Edit"
                                                        >
                                                            <Edit2 className="h-4 w-4" />
                                                        </button>

                                                        <button
                                                            onClick={() => handleDeleteNoSay(list)}
                                                            disabled={deleteNoSayMutation.isLoading}
                                                            className="text-red-600 hover:text-red-900 disabled:opacity-50"
                                                            title="Delete"
                                                        >
                                                            <Trash2 className="h-4 w-4" />
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>

            <AddNoSayModal
                isOpen={createModalOpen}
                onClose={() => setCreateModalOpen(false)}
                noSay={selectedNoSay}
                onSuccess={handleCreateSuccess}
            />
        </div>
    )
}

export default NoSayList
